import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { getPublicPortfolios } from "../axios/portfolioAPI";

const LeaderCard = ({ member, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.article
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl"
    >
      <div className="relative flex h-80 w-full items-center justify-center overflow-hidden bg-slate-100 sm:h-[340px] md:h-[370px] lg:h-[390px]">
        <img
          src={member.image}
          alt={`${member.name} - ${member.role}`}
          style={{ objectPosition: member.imagePosition || "center center" }}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-blue-300">
            {member.role}
          </p>
          <h3 className="mt-1 text-2xl font-bold text-white">{member.name}</h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        {member.focus ? (
          <div className="mb-4">
            <span className="inline-flex rounded-full bg-blue-50 px-3 py-1.5 text-[10px] font-semibold leading-4 text-blue-700">
              {member.focus}
            </span>
          </div>
        ) : null}

        <p
          className="text-sm leading-6 text-slate-600"
          style={
            expanded
              ? {}
              : {
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 7,
                  overflow: "hidden",
                }
          }
        >
          {member.bio}
        </p>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-blue-600 transition-colors duration-300 hover:text-blue-800"
        >
          {expanded ? "See Less" : "See More"}
          <ArrowRight className={`h-3.5 w-3.5 transition-transform duration-300 ${expanded ? "rotate-90" : ""}`} />
        </button>
      </div>
    </motion.article>
  );
};

const Portfolio = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const response = await getPublicPortfolios();
        setPortfolios(response?.data || response?.portfolios || []);
      } catch (error) {
        console.error("Portfolio API error:", error);
        setPortfolios([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);

  return (
    <>
      <Helmet>
        <title>Portfolio | Suraksha 24/7</title>
      </Helmet>

      <div className="min-h-screen overflow-x-hidden bg-white font-sans text-slate-900 selection:bg-blue-100">
        <section className="w-full bg-gray-100 px-4 py-10 sm:px-6 lg:py-14">
          <div className="mx-auto mb-10 max-w-3xl text-center lg:mb-12">
            <div className="mb-4 inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
              <ShieldCheck className="mr-2 h-3 w-3" /> Meet Our Leaders
            </div>
            <h2 className="text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Visionaries <span className="text-blue-600">Behind Our Success</span>
            </h2>
          </div>

          {loading ? (
            <div className="text-center text-slate-500 font-semibold">Loading...</div>
          ) : (
            <div className="mx-auto grid max-w-[1600px] grid-cols-1 items-start gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {portfolios.map((member, index) => (
                <LeaderCard key={member._id || member.name} member={member} index={index} />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default Portfolio;